import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Header from '../components/common/layout/Header';
import Footer from '../components/common/layout/Footer';
import Disclaimer from '../components/common/layout/Disclaimer';
import Hotline from '../components/common/layout/Hotline';
import ShareBtn from '../components/common/layout/ShareBtn';
import useMatomo from '../hooks/useMatomo';

import warrantMarketRoutes from './routes/warrantMarketRoutes';
import warrantToolRoutes from './routes/warrantToolRoutes';
import warrantEducationRoutes from './routes/warrantEducationRoutes';
import investorAcademyRoutes from './routes/investorAcademyRoutes';
import miscRoutes from './routes/miscRoutes';

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    Loading...
  </div>
);

const RouteErrorFallback = () => (
  <div style={{ padding: '40px', textAlign: 'center' }}>
    <h2>Component loading failed</h2>
    <p>Please check if there are any errors in the component files。</p>
  </div>
);

function ErrorBoundaryWrapper({ children }) {
  const [hasError, setHasError] = React.useState(false);
  
  React.useEffect(() => {
    const errorHandler = (error) => {
      console.error('Route component error:', error);
      setHasError(true);
    };
    
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);
  
  if (hasError) {
    return <RouteErrorFallback />;
  }
  
  return children;
}

function RouteWithTitle({
  title,
  description,
  keyword,
  ogUrl,
  ogType,
  ogTitle,
  ogDescription,
  ogImage,
  facebookDomain,
  element,
}) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {keyword && <meta name="keywords" content={keyword} />}
        {ogUrl && <meta property="og:url" content={ogUrl} />}
        {ogType && <meta property="og:type" content={ogType} />}
        {ogTitle && <meta property="og:title" content={ogTitle} />}
        {ogDescription && <meta property="og:description" content={ogDescription} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        {facebookDomain && (
          <meta name="facebook-domain-verification" content={facebookDomain} />
        )}
      </Helmet>
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundaryWrapper>
          {element}
        </ErrorBoundaryWrapper>
      </Suspense>
    </>
  );
}

function MatomoTracker() {
  useMatomo();
  return null;
}

const allRoutes = [
  ...warrantMarketRoutes,
  ...warrantToolRoutes,
  ...warrantEducationRoutes,
  ...investorAcademyRoutes,
  ...miscRoutes,
];

function UnAuthorisedApp() {
  const location = useLocation();
  const shouldHideLayout = location.pathname.includes("moneyflowchart");

  return (
    <>
      <MatomoTracker />
      {!shouldHideLayout && <Header />}

      <Routes location={location} key={location.pathname}>
        {allRoutes.map((route) => {
          const { element: routeElement, path, ...routeMeta } = route;

          return (
            <Route
              key={path}
              path={path}
              element={
                <RouteWithTitle
                  {...routeMeta}
                  element={routeElement}
                />
              }
            />
          );
        })}

        <Route
          path="*"
          element={
            <Navigate
              to="/home"
              state={{ from: location }}
              replace
            />
          }
        />
      </Routes>

      {!shouldHideLayout && <Footer />}
      {!shouldHideLayout && <div id="xsTest" className="visible-xs" />}
      {!shouldHideLayout && <div id="smTest" className="visible-sm" />}
      {!shouldHideLayout && <div id="mdTest" className="visible-md" />}
      {!shouldHideLayout && <div id="lgTest" className="visible-lg" />}
      {!shouldHideLayout && <Disclaimer />}
      {!shouldHideLayout && <Hotline />}
      {!shouldHideLayout && <ShareBtn Location={location} />}
    </>
  );
}

export default UnAuthorisedApp;
